/**
 * BoundsEditor — min/max weight clamps, compact inline single row.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { ArrowLeftRight, Info } from 'lucide-react';

import { NumberField } from '@/components/ui';

export interface BoundsEditorProps {
  minWeight: number;
  maxWeight: number;
  onChange: (minWeight: number, maxWeight: number) => void;
}

const SCALE_EXPLAINER =
  'Per-item floor and ceiling, expressed in % of the layer budget. ' +
  'Math runs on a finer internal scale so long-tail items still round cleanly; ' +
  'every share you see in the UI — clamps, weights, deltas — is the same %.';

const BoundsEditor: FC<BoundsEditorProps> = ({ minWeight, maxWeight, onChange }) => (
  <div className="inline-flex h-7 items-center gap-1.5 rounded-md border border-border bg-muted/20 px-2 text-[11px]">
    <ArrowLeftRight className="h-3 w-3 shrink-0 text-muted-foreground" />
    <span className="shrink-0 text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">
      Clamps
    </span>
    <BoundInput value={minWeight} min={0} max={maxWeight - 1} onChange={(v) => onChange(v, maxWeight)} />
    <span className="text-muted-foreground">–</span>
    <BoundInput value={maxWeight} min={minWeight + 1} max={100} onChange={(v) => onChange(minWeight, v)} />
    <span className="ml-0.5 text-[10px] text-muted-foreground/60">per item</span>
    <span title={SCALE_EXPLAINER} className="cursor-help text-muted-foreground/60">
      <Info className="h-3 w-3" aria-hidden />
    </span>
  </div>
);

const BoundInput: FC<{
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}> = ({ value, min, max, onChange }) => (
  <span className="inline-flex items-center">
    <NumberField
      value={value}
      min={min}
      max={max}
      onChange={onChange}
      aria-label="Weight clamp, percent"
      className="h-5 w-10 rounded border border-input bg-background px-1 text-right text-[11px] tabular-nums focus:outline-none focus:ring-1 focus:ring-ring"
    />
    <span className="ml-0.5 text-[10px] text-muted-foreground">%</span>
  </span>
);

export default BoundsEditor;
